import { type ChangeEvent, type FC, useMemo, useState } from "react";
import { Box, Divider, styled, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { type GridColDef } from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";
import { useSearchParams } from "react-router-dom";
import { SearchTextField } from "@packsize/components";
import { useFeatureFlags } from "@packsize/feature-flags";

import { useClosingRequiredCartons } from "@/views/closing/hooks/use-closing-required-cartons";
import { useClosingFailedJobs } from "@/views/closing/hooks/use-closing-failed-jobs";
import { useMachines } from "@/hooks/machines";
import type { ClosingCarton, ClosingScanFailed, Machine } from "@/schemas";
import { formatAgeMinutes } from "@/utils/luxon";
import { FailedRetry } from "@/components/failed-retry";
import { useDebouncedSearch } from "@/hooks/use-debounced-search";

import { useClosingScanFailed } from "../hooks/use-closing-scan-failed";
import { getClosingScanFailureReason } from "../utils";

import { AwaitingClosingActionMenu } from "./awaiting-closing-action-menu";
import { ClosingEmptyState } from "./closing-empty-state";
import { ClosingCartonsTable } from "./closing-cartons-table";
import { ScanRejectedTable } from "./scan-rejected-table";
import { ScanRejectedActionMenu } from "./scan-rejected-action-menu";

type TabType = "available" | "failed" | "scanRejected";

const SelectableBox = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	padding: theme.spacing(1),
	paddingX: theme.spacing(2),
	cursor: "pointer",
}));

export const AwaitingClosingContainer: FC = () => {
	const intl = useIntl();
	const [searchParams, setSearchParams] = useSearchParams();
	const { isFeatureFlagEnabled } = useFeatureFlags();
	const scanRejectedEnabled = isFeatureFlagEnabled("closing-scan-failed");

	const getTabFromQueryString = (): TabType => {
		const tab = searchParams.get("tab");
		if (tab === "failed") return "failed";
		if (tab === "scanRejected") return "scanRejected";
		return "available";
	};

	const selectedTab = getTabFromQueryString();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const { inputValue, filter, handleInputChange, handleSearch } = useDebouncedSearch<null>({ emptyValue: null });

	const setSelectedTab = (newTab: TabType) => {
		setSearchParams({ tab: newTab });
	};

	const { machines } = useMachines({ refetchInterval: false });
	const machineList = useMemo<Machine[]>(() => (machines ?? []) as Machine[], [machines]);

	const { data: closingRequiredData, error: requiredCartonsError } = useClosingRequiredCartons({
		filter,
		pageNumber: page + 1,
		pageSize: rowsPerPage,
	});

	const { data: closingFailedData, error: failedCartonsError } = useClosingFailedJobs({
		filter,
		pageNumber: page + 1,
		pageSize: rowsPerPage,
	});

	const { data: closingScanFailedData, error: closingScanFailedError } = useClosingScanFailed({
		pageNumber: page + 1,
		pageSize: rowsPerPage,
		enabled: scanRejectedEnabled,
	});

	const totalAwaitingClosingCartons = closingRequiredData?.totalResults || 0;
	const totalFailedCartons = closingFailedData?.totalResults || 0;
	const awaitingClosingCartons = closingRequiredData?.results || [];
	const failedCartons = closingFailedData?.results || [];
	const failedClosingScans = closingScanFailedData?.items || [];
	const totalFailedClosingScans = closingScanFailedData?.totalCount || 0;
	const hasScanRejectedAliases = failedClosingScans.some((row) => row.alias);

	const handlePageChange = (_event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	if (requiredCartonsError || failedCartonsError || closingScanFailedError) {
		return <FailedRetry retry={() => window.location.reload()} />;
	}

	const requiredColumns: GridColDef<ClosingCarton>[] = [
		{
			field: "alias",
			headerName: intl.formatMessage({ id: "Title", defaultMessage: "Title" }),
			flex: 1,
			minWidth: 150,
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "identifier",
			headerName: intl.formatMessage({ id: "Barcode", defaultMessage: "Barcode" }),
			flex: 1,
			minWidth: 150,
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "age",
			headerName: intl.formatMessage({ id: "Age", defaultMessage: "Age" }),
			flex: 1,
			minWidth: 120,
			valueGetter: (_value, row) => formatAgeMinutes(row.upstreamCompletedDateUTC),
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "closingId",
			headerName: "",
			width: 60,
			disableColumnMenu: true,
			sortable: false,
			headerAlign: "right",
			align: "right",
			renderCell: (params) => <AwaitingClosingActionMenu closingCarton={params.row} />,
		},
	];

	const failedColumns: GridColDef<ClosingCarton>[] = [
		{
			field: "alias",
			headerName: intl.formatMessage({ id: "Title", defaultMessage: "Title" }),
			flex: 1,
			minWidth: 150,
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "identifier",
			headerName: intl.formatMessage({ id: "Barcode", defaultMessage: "Barcode" }),
			flex: 1,
			minWidth: 150,
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "failureReason",
			headerName: intl.formatMessage({ id: "Failure Reason", defaultMessage: "Failure Reason" }),
			flex: 2,
			minWidth: 200,
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "lastFailedClosingDateUTC",
			headerName: intl.formatMessage({ id: "Failure Date", defaultMessage: "Failure Date" }),
			flex: 1,
			minWidth: 180,
			valueGetter: (_value, row) =>
				row.lastFailedClosingDateUTC
					? DateTime.fromJSDate(row.lastFailedClosingDateUTC).toFormat("MM/dd/yy, HH:mm:ss")
					: "",
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "closingId",
			headerName: "",
			width: 60,
			disableColumnMenu: true,
			sortable: false,
			headerAlign: "right",
			align: "right",
			renderCell: (params) => <AwaitingClosingActionMenu closingCarton={params.row} />,
		},
	];

	const scanRejectedColumns: GridColDef<ClosingScanFailed>[] = [
		{
			field: "scannedIdentifier",
			headerName: intl.formatMessage({ id: "Barcode", defaultMessage: "Barcode" }),
			flex: 1,
			minWidth: 150,
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "eventUtcTimeStamp",
			headerName: intl.formatMessage({ id: "Date", defaultMessage: "Date" }),
			flex: 1,
			minWidth: 150,
			valueGetter: (_value, row) =>
				row.eventUtcTimeStamp ? DateTime.fromJSDate(row.eventUtcTimeStamp).toFormat("MM/dd/yy, HH:mm:ss") : "",
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "failureReason",
			headerName: intl.formatMessage({ id: "Reason", defaultMessage: "Reason" }),
			flex: 1,
			minWidth: 150,
			valueGetter: (_value, row) => getClosingScanFailureReason(row.failureReason, intl),
			disableColumnMenu: true,
			sortable: false,
		},
		...(hasScanRejectedAliases
			? [
					{
						field: "alias",
						headerName: intl.formatMessage({ id: "Title", defaultMessage: "Title" }),
						flex: 1,
						minWidth: 150,
						valueGetter: (_value: unknown, row: ClosingScanFailed) =>
							row.alias ? row.alias : intl.formatMessage({ id: "Unknown", defaultMessage: "Unknown" }),
						disableColumnMenu: true,
						sortable: false,
					},
				]
			: []),
		{
			field: "machineId",
			headerName: intl.formatMessage({ id: "Machine Name", defaultMessage: "Machine Name" }),
			flex: 1,
			minWidth: 150,
			valueGetter: (_value, row) => machineList.find((machine) => machine.id === row.machineId)?.alias || "",
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "closingId",
			headerName: "",
			width: 60,
			disableColumnMenu: true,
			sortable: false,
			headerAlign: "right",
			align: "right",
			renderCell: (params) => <ScanRejectedActionMenu scan={params.row} />,
		},
	];

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					paddingY: 1.5,
					paddingX: 2,
					borderRadius: 1,
					flexShrink: 0,
				}}
			>
				<Box sx={{ display: "flex", gap: 1 }}>
					<SelectableBox
						onClick={() => setSelectedTab("available")}
						sx={{
							backgroundColor: selectedTab === "available" ? "grey.300" : "transparent",
							borderRadius: 1,
							"&:hover": {
								backgroundColor: selectedTab === "available" ? "grey.300" : "grey.100",
							},
						}}
					>
						<Typography variant="body2">
							<FormattedMessage
								id="Closing Required ({totalResults})"
								defaultMessage="Closing Required ({totalResults})"
								values={{ totalResults: totalAwaitingClosingCartons }}
							/>
						</Typography>
					</SelectableBox>
					<SelectableBox
						onClick={() => setSelectedTab("failed")}
						sx={{
							backgroundColor: selectedTab === "failed" ? "grey.300" : "transparent",
							borderRadius: 1,
							"&:hover": {
								backgroundColor: selectedTab === "failed" ? "grey.300" : "grey.100",
							},
						}}
					>
						<Typography variant="body2">
							<FormattedMessage
								id="Closing Failed ({totalResults})"
								defaultMessage="Closing Failed ({totalResults})"
								values={{ totalResults: totalFailedCartons }}
							/>
						</Typography>
					</SelectableBox>
					{scanRejectedEnabled && (
						<SelectableBox
							onClick={() => setSelectedTab("scanRejected")}
							sx={{
								backgroundColor: selectedTab === "scanRejected" ? "grey.300" : "transparent",
								borderRadius: 1,
								"&:hover": {
									backgroundColor: selectedTab === "scanRejected" ? "grey.300" : "grey.100",
								},
							}}
						>
							<Typography variant="body2">
								<FormattedMessage
									id="Scan Rejected ({totalResults})"
									defaultMessage="Scan Rejected ({totalResults})"
									values={{ totalResults: totalFailedClosingScans }}
								/>
							</Typography>
						</SelectableBox>
					)}
				</Box>
				{selectedTab !== "scanRejected" && (
					<SearchTextField
						size="small"
						value={inputValue}
						onChange={handleInputChange}
						onSubmit={handleSearch}
						placeholder={intl.formatMessage({ id: "Title or Barcode...", defaultMessage: "Title or Barcode..." })}
						autoSelectOnFocus
					/>
				)}
			</Box>
			<Divider sx={{ marginTop: 1, flexShrink: 0 }} />
			<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
				{selectedTab === "available" && (
					<ClosingCartonsTable
						cartons={awaitingClosingCartons}
						columns={requiredColumns}
						totalCount={totalAwaitingClosingCartons}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handleRowsPerPageChange}
						emptyState={
							<ClosingEmptyState
								message={intl.formatMessage({
									id: "Nothing is Waiting to Close",
									defaultMessage: "Nothing is Waiting to Close",
								})}
							/>
						}
					/>
				)}
				{selectedTab === "failed" && (
					<ClosingCartonsTable
						cartons={failedCartons}
						columns={failedColumns}
						totalCount={totalFailedCartons}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handleRowsPerPageChange}
						emptyState={
							<ClosingEmptyState
								message={intl.formatMessage({
									id: "Nothing has Failed to Close",
									defaultMessage: "Nothing has Failed to Close",
								})}
							/>
						}
					/>
				)}
				{selectedTab === "scanRejected" && (
					<ScanRejectedTable
						scans={failedClosingScans}
						columns={scanRejectedColumns}
						totalCount={totalFailedClosingScans}
						page={page}
						rowsPerPage={rowsPerPage}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handleRowsPerPageChange}
						emptyState={
							<ClosingEmptyState
								message={intl.formatMessage({
									id: "No closing scans have failed",
									defaultMessage: "No closing scans have failed",
								})}
							/>
						}
					/>
				)}
			</Box>
		</Box>
	);
};
